import { apiFetch } from '@sdc/auth';
import { useCallback, useEffect, useState } from 'react';
import { PEOPLE_API_ORIGIN } from '../config';

export interface Employee {
  /** erecno aus Zoho People. */
  id: string;
  name: string;
  email: string | null;
  aktiv: boolean;
}

export type EmployeeState =
  /** Noch nicht angefordert – der Abruf startet erst auf Klick. */
  | { status: 'idle' }
  /** Keine Quelle konfiguriert – Panel zeigt „Anbindung folgt". */
  | { status: 'unconfigured' }
  | { status: 'loading' }
  | { status: 'ready'; employees: Employee[] }
  | { status: 'error'; message: string };

function parseEmployees(raw: unknown): Employee[] {
  if (!Array.isArray(raw)) return [];
  const out: Employee[] = [];
  for (const e of raw) {
    if (!e || typeof e !== 'object') continue;
    const r = e as Record<string, unknown>;
    if (typeof r.id !== 'string' || !r.id) continue;
    out.push({
      id: r.id,
      name: typeof r.name === 'string' ? r.name : '',
      email: typeof r.email === 'string' && r.email ? r.email.toLowerCase() : null,
      // Fehlt die Angabe, gilt die Person als aktiv – jemanden versehentlich
      // auszublenden wäre der teurere Fehler.
      aktiv: r.aktiv !== false,
    });
  }
  return out;
}

/**
 * Der Personalstamm aus der People-App (`/api/employees`) für den Abgleich
 * „wer hat noch keinen Hub-Zugang".
 *
 * Kommt bewusst nicht aus dem Identity-Projekt: die Mitarbeiterdaten liegen in
 * Zoho People, und People hat die Anbindung samt Token bereits. Ein zweiter
 * Zoho-Zugang im Hub wäre ein zweiter Ort, an dem Zugangsdaten liegen.
 *
 * Wird **nicht** beim Öffnen der Seite geladen, sondern erst auf Anforderung:
 * der Endpunkt fragt Zoho live ab und liefert Beschäftigtendaten. Beides sind
 * Gründe, das nicht nebenbei bei jedem Seitenaufruf zu tun.
 */
export function useEmployees(): { state: EmployeeState; laden: () => void } {
  const [state, setState] = useState<EmployeeState>(() =>
    PEOPLE_API_ORIGIN ? { status: 'idle' } : { status: 'unconfigured' },
  );
  // Zaehler statt Flag: ein `true`, das schon `true` ist, loest keinen Effekt
  // aus — der zweite Klick auf "laden" waere wirkungslos.
  const [anforderung, setAnforderung] = useState(0);

  const laden = useCallback(() => {
    if (!PEOPLE_API_ORIGIN) return;
    setState({ status: 'loading' });
    setAnforderung((n) => n + 1);
  }, []);

  useEffect(() => {
    if (anforderung === 0 || !PEOPLE_API_ORIGIN) return;
    let active = true;

    void (async () => {
      try {
        const res = await apiFetch(`${PEOPLE_API_ORIGIN}/api/employees`);
        if (!active) return;

        // Endpoint noch nicht ausgerollt: kein Fehler, sondern „folgt".
        if (res.status === 404) {
          setState({ status: 'unconfigured' });
          return;
        }
        if (res.status === 401 || res.status === 403) {
          setState({
            status: 'error',
            message: 'People lässt den Zugriff nicht zu – dort fehlt die Admin-Rolle.',
          });
          return;
        }
        if (!res.ok) {
          setState({ status: 'error', message: `People antwortet ${res.status}` });
          return;
        }
        setState({ status: 'ready', employees: parseEmployees(await res.json()) });
      } catch {
        if (active) setState({ status: 'error', message: 'People ist nicht erreichbar' });
      }
    })();

    return () => {
      active = false;
    };
  }, [anforderung]);

  return { state, laden };
}
